/**
 * navigation/planGate.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Which product WEBSITES the signed-in org may open.
 *
 * GET /v1/me carries `subscribed_products`: the products the org bought. A plan
 * includes products it only uses underneath (SK Fleet keeps its customers in
 * CRM), and those stay API-only — the Fleet console keeps working, but a Fleet
 * client should not land on the CRM website. This file is the one place that
 * reads that list.
 *
 * Fails OPEN: the list missing, null, or not an array means "no restriction".
 * The platform sends null while its PRODUCT_UI_GATE_ENABLED switch is off, for
 * the trial plan, and for Softknack staff. A wrong block locks a paying
 * customer out; a wrong allow does not.
 */

export const NOT_IN_PLAN_PATH = '/not-in-plan';

/** The `subscribed_products` list from a /v1/me body (envelope or bare), or null. */
export function subscribedProductsFrom(me: unknown): string[] | null {
  const body = me as { data?: { subscribed_products?: unknown }; subscribed_products?: unknown } | null;
  const list = body?.data?.subscribed_products ?? body?.subscribed_products;
  return Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string') : null;
}

/** True only when the org has a list and this product's website is not on it. */
export function isNotInPlan(me: unknown, product: string): boolean {
  // The platform's own console is never a product the org buys.
  if (!product || product === 'platform') return false;
  const list = subscribedProductsFrom(me);
  return list !== null && !list.includes(product);
}

export function notInPlanUrl(product: string): string {
  return `${NOT_IN_PLAN_PATH}?product=${encodeURIComponent(product)}`;
}

/** The org id from a /v1/me body, for the block log line. */
export function orgIdFrom(me: unknown): string | null {
  const body = me as { data?: { org?: { id?: unknown } }; org?: { id?: unknown } } | null;
  const id = body?.data?.org?.id ?? body?.org?.id;
  return typeof id === 'string' ? id : null;
}

const DISPLAY_NAMES: Record<string, string> = {
  crm: 'SK CRM',
  cal: 'SK Cal',
  people: 'SK People',
  drive: 'SK Drive',
  billing: 'SK Billing',
  catalog: 'SK Catalog',
  inventory: 'SK Inventory',
  notification: 'SK Notifications',
  chat: 'SK Chat',
  tickets: 'SK Tickets',
  queue: 'SK Queue',
  sign: 'SK Sign',
  hire: 'SK Hire',
  upsell: 'SK Upsell',
  voice: 'SK Voice',
  bridge: 'SK Bridge',
  works: 'SK Works',
  watch: 'SK Watch',
  company_cash: 'SK Company Cash',
  fleet: 'SK Fleet',
  salon: 'SK Salon',
  hosp: 'SK Hospitality',
  social: 'SK Social',
  franchise: 'SK Franchise',
  hq: 'SK HQ Console',
};

export function productDisplayName(product: string | null | undefined): string {
  if (!product) return 'This product';
  return DISPLAY_NAMES[product] ?? product;
}

// softknack.com/<slug>/pricing — the website slug follows the plan ids
// (calendar_starter, notifications_growth), which differ from two product slugs.
// Products with no pricing page of their own (fleet, watch, company_cash…) are
// left out, and the screen hides the button rather than linking to a 404.
const PRICING_SLUGS: Record<string, string> = {
  crm: 'crm',
  cal: 'calendar',
  people: 'people',
  drive: 'drive',
  billing: 'billing',
  catalog: 'catalog',
  inventory: 'inventory',
  notification: 'notifications',
  chat: 'chat',
  tickets: 'tickets',
  queue: 'queue',
  sign: 'sign',
  hire: 'hire',
  upsell: 'upsell',
  voice: 'voice',
  works: 'works',
  salon: 'salon',
};

export function pricingUrlFor(product: string | null | undefined): string | null {
  const slug = product ? PRICING_SLUGS[product] : undefined;
  return slug ? `https://softknack.com/${slug}/pricing` : null;
}

// ─── External products ───────────────────────────────────────────────────────
//
// `crm`, `catalog` and `voice` are screens INSIDE this shell (screens/products/*,
// seeded from data/platformSeed). SK Social is not: it is a separate app on its
// own origin, so its entry in the product nav has to leave the platform rather
// than route in-shell.
//
// The origin is NOT hardcoded here. It comes from the same allow-list the
// post-auth redirect already validates against — GET /v1/products/redirect-targets,
// derived server-side from the product registry (lib/products/registry.ts →
// `APP_ORIGIN_SOCIAL`). One source of truth, so a product that moves host, or is
// unconfigured in an environment, cannot leave a dead button behind: an origin
// the allow-list does not return simply does not appear.
//
// The session rides the shared `.softknack.com` httpOnly cookie, so the user
// arrives already signed in; no handoff params are needed in this direction.
// (INTEGRATING_PRODUCTS.md §1 — the cookie is sent automatically.)

/** Products that live outside this shell, with the registry slug to resolve. */
export const EXTERNAL_PRODUCTS: { id: string; label: string }[] = [
  { id: 'social', label: 'Social' },
];

/**
 * The product nav entries this org should see.
 *
 * In-shell products keep today's behaviour exactly. External ones are added
 * only when BOTH are true:
 *   • the org's plan includes them (`subscribed_products`), using the same
 *     fail-open rule as the rest of this file — a missing list means no
 *     restriction, because a wrong block locks a paying customer out; and
 *   • the allow-list has an origin for them, so the button always goes
 *     somewhere real.
 *
 * `targets` is the resolved allow-list (config/products.ts). Passing it in
 * keeps this file free of network calls and testable as pure logic.
 */
export function productNavEntries(
  me: unknown,
  targets: Record<string, string> | null,
  inShell: { id: string; label: string }[],
): { id: string; label: string; href?: string }[] {
  const external = EXTERNAL_PRODUCTS
    .filter((p) => !isNotInPlan(me, p.id))
    .map((p) => ({ ...p, href: targets?.[p.id] }))
    .filter((p): p is { id: string; label: string; href: string } => !!p.href);
  return [...inShell, ...external];
}
